import { useEffect, useState } from "react";
import { getMarketClock } from "@/finance";
import { IconChevronDown } from "@/platform/ui";
import { useTz } from "@/platform/client";
import { useHoverPopover } from "@/platform/client";

export function Clocks() {
  const [now, setNow] = useState(() => new Date());
  const { open: popoverOpen, wrapRef, toggleTap, pointerHandlers } = useHoverPopover();
  const localTz = useTz();

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const clock = getMarketClock(now, localTz);
  const open = clock.session.isOpen;
  const statusColor = open ? "var(--color-positive)" : "var(--color-muted)";

  return (
    <div className="flex items-stretch gap-2 sm:gap-2.5">
      {/* ---- NYSE market clock + popover (hover on desktop, tap on touch) ---- */}
      <div ref={wrapRef} className="relative" {...pointerHandlers}>
        <button
          type="button"
          onClick={toggleTap}
          aria-expanded={popoverOpen}
          className="flex h-full cursor-default items-center justify-between gap-2 rounded-[10px] border border-line-strong bg-surface-2 px-2.5 py-1.5 sm:gap-3 sm:px-3.5"
        >
          <div className="flex flex-col gap-[5px] leading-none">
            <span className="text-[13px] font-extrabold tracking-wide text-ink">NYSE</span>
            <div className="flex items-center gap-1.5">
              <span
                className={open ? "pulse" : ""}
                style={{ width: 6, height: 6, borderRadius: 9999, background: statusColor }}
              />
              <span
                className="text-[9px] font-extrabold tracking-wider"
                style={{ color: statusColor }}
              >
                {open ? "OPEN" : "CLOSED"}
              </span>
            </div>
          </div>
          <span className="h-[30px] w-px bg-line-strong" />
          <div className="flex flex-col items-end gap-[3px] leading-none">
            <span className="mono text-[15px] font-bold text-ink">{clock.nyseTime}</span>
            <span className="text-[9.5px] font-semibold text-muted">{clock.nyseDate}</span>
          </div>
          <IconChevronDown size={11} className="text-faint" />
        </button>

        {popoverOpen && (
          <div className="absolute right-0 top-[calc(100%+8px)] z-40 w-full rounded-xl border border-line-strong bg-raised p-4 shadow-2xl">
            <div className="mb-2.5 flex items-baseline justify-between gap-2 rounded-[10px] border border-line bg-surface px-3 py-2.5">
              <span className="text-[11px] font-bold tracking-wide text-muted">
                {clock.countdownLabel}
              </span>
              <span className="mono text-[11.5px] font-bold leading-none text-ink">
                {clock.countdown}
              </span>
            </div>
            <div className="rounded-[10px] border border-line bg-surface px-3 py-3">
              <div className="mb-2.5 text-[9.5px] font-extrabold uppercase tracking-[0.09em] text-muted">
                {clock.eventLabel}
              </div>
              <div className="mb-2 flex items-center justify-between gap-3">
                <span className="text-[11px] font-semibold text-muted">Day</span>
                <span className="text-[11.5px] font-bold text-ink">{clock.eventEtDay}</span>
              </div>
              <div className="mb-2 flex items-center justify-between gap-3">
                <span className="text-[11px] font-semibold text-muted">New York</span>
                <span className="mono text-[11.5px] font-bold text-ink">{clock.eventEt}</span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-[11px] font-semibold text-muted">{clock.tzName}</span>
                <span className="mono text-[11.5px] font-bold text-ink">{clock.eventLocal}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ---- local clock (time centered) ---- */}
      <div className="flex flex-col items-center justify-center gap-[3px] rounded-[10px] border border-line-strong bg-surface-2 px-2.5 py-1.5 leading-none sm:px-3.5">
        <span className="mono text-[15px] font-bold text-ink">{clock.localTime}</span>
        <span className="text-[9.5px] font-semibold text-muted">{clock.localDate}</span>
      </div>
    </div>
  );
}
